import React from 'react';

import PropTypes from 'prop-types';

import { useDeprecate, variantSwitch } from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';

import Button from './Button';
import { StylableButtonProps } from './consts';
import BasicButton from './templates/BasicButton';
import LinkButton from './templates/LinkButton';

const StyleType = {
  DEFAULT: 'default',
  PRIMARY: 'primary',
  DANGER: 'danger',
  CANCEL: 'cancel',
  LINK: 'link',
  LINK_SECONDARY: 'link-secondary',
  ICON: 'icon',
  ICON_MINIMAL: 'icon-minimal',
  NAVIGATION: 'navigation',
  INLINE_LINK: 'inline-link',
} as const;

type StyleTypeType = (typeof StyleType)[keyof typeof StyleType];

function getVariant(styleType?: StyleTypeType) {
  switch (styleType) {
    case StyleType.PRIMARY:
    case StyleType.DANGER:
      return Button.VARIANTS.PRIMARY;
    case StyleType.DEFAULT:
      return Button.VARIANTS.SECONDARY;
    case StyleType.CANCEL:
      return Button.VARIANTS.TERTIARY;
    default:
      return undefined;
  }
}

const ButtonAdapter = ({
  className,
  children,
  styleType,
  ...props
}: StylableButtonProps & {
  styleType?: StyleTypeType;
}) => {
  useDeprecate(true, '@adsk/alloy-react-button', 'ButtonAdapter', 'Button');

  let ButtonComponent: typeof Button | typeof BasicButton | typeof LinkButton =
    Button;
  if (
    styleType === StyleType.ICON ||
    styleType === StyleType.ICON_MINIMAL ||
    styleType === StyleType.NAVIGATION
  ) {
    ButtonComponent = BasicButton;
  }

  if (
    styleType === StyleType.LINK ||
    styleType === StyleType.LINK_SECONDARY ||
    styleType === StyleType.INLINE_LINK
  ) {
    ButtonComponent = LinkButton;
  }

  return (
    <ButtonComponent
      className={className}
      variant={getVariant(styleType)}
      alert={styleType === StyleType.DANGER}
      style={variantSwitch({
        statement: styleType,
        variants: {
          [StyleType.LINK_SECONDARY]: {
            color: theme.colors.charcoal600,
          },
          [StyleType.NAVIGATION]: {
            color: theme.colors.black,
          },
          [StyleType.INLINE_LINK]: {
            padding: 0,
          },
          [StyleType.ICON_MINIMAL]: {
            padding: 0,
          },
        },
      })}
      {...props}
    >
      {children}
    </ButtonComponent>
  );
};

ButtonAdapter.StyleType = StyleType;
ButtonAdapter.getVariant = getVariant;

ButtonAdapter.propTypes = {
  /** custom className for the button */
  className: PropTypes.string,
  /** component's children */
  children: PropTypes.any,
  /** HTML type attribute of the button, default to 'button' */
  type: PropTypes.string,
  /** style for the button. Can be one of Button.StyleType */
  styleType: PropTypes.oneOf(Object.values(ButtonAdapter.StyleType)),
};

export default ButtonAdapter;
